import type { OemRecord } from '../../lib/oem-client';
export type Deadline =
  | { kind: 'missing' }
  | { kind: 'invalid' }
  | { kind: 'date' | 'instant'; at: number; source: string };
export type TaskCategory = 'overdue' | 'today' | 'upcoming' | 'missing' | 'invalid';
export const taskCategories: { key: TaskCategory; label: string }[] = [
  { key: 'overdue', label: 'เลยกำหนด' },
  { key: 'today', label: 'ครบกำหนดวันนี้' },
  { key: 'upcoming', label: 'ยังไม่ถึงกำหนด' },
  { key: 'missing', label: 'ยังไม่กำหนดวันส่ง' },
  { key: 'invalid', label: 'ตรวจวันที่อีกครั้ง' },
];
function calendarValid(source: string): boolean {
  const year = Number(source.slice(0, 4)),
    month = Number(source.slice(5, 7)),
    day = Number(source.slice(8, 10));
  const days = [
    31,
    year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  return year >= 1 && month >= 1 && month <= 12 && day >= 1 && day <= (days[month - 1] ?? 0);
}
export function parseDeadline(value: unknown): Deadline {
  if (value === undefined || value === null || (typeof value === 'string' && value.trim() === ''))
    return { kind: 'missing' };
  if (typeof value !== 'string' || !calendarValid(value)) return { kind: 'invalid' };
  if (/^\d{4}-\d{2}-\d{2}$/.test(value))
    return { kind: 'date', at: Date.parse(value + 'T23:59:59.999+07:00'), source: value };
  if (
    !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,6})?)?(?:Z|[+-](?:0\d|1[0-4]):[0-5]\d)$/.test(
      value,
    ) ||
    /[+-]14:(?!00)/.test(value) ||
    value.endsWith('-00:00')
  )
    return { kind: 'invalid' };
  const at = Date.parse(value);
  return Number.isFinite(at) ? { kind: 'instant', at, source: value } : { kind: 'invalid' };
}
export function bangkokDay(at: number): string {
  return new Date(at + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
export function deadlineCategory(deadline: Deadline, now: number): TaskCategory {
  if (deadline.kind === 'missing' || deadline.kind === 'invalid') return deadline.kind;
  if (deadline.at < now) return 'overdue';
  return bangkokDay(deadline.at) === bangkokDay(now) ? 'today' : 'upcoming';
}
export function deadlineText(deadline: Deadline): string {
  if (deadline.kind === 'missing') return 'ยังไม่กำหนดวันส่ง';
  if (deadline.kind === 'invalid') return 'รูปแบบวันไม่ชัดเจนหรือวันที่ไม่ถูกต้อง';
  const date = new Intl.DateTimeFormat('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(deadline.kind === 'instant' ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(deadline.at);
  return `${deadline.kind === 'date' ? 'สิ้นวัน ' : ''}${date} · กรุงเทพฯ`;
}
export function latestVisibleByRoot(records: OemRecord[]): OemRecord[] {
  const latest = new Map<string, OemRecord>();
  const createdOrder = (record: OemRecord, previous: OemRecord): number => {
    const precision = (item: OemRecord) => {
      const date = parseDeadline(item.createdAt);
      return date.kind === 'instant'
        ? {
            at: date.at,
            micros: Number((/\.(\d{1,6})/.exec(date.source)?.[1] ?? '').padEnd(6, '0').slice(3)),
          }
        : { at: -Infinity, micros: 0 };
    };
    const a = precision(record),
      b = precision(previous),
      difference = a.at - b.at;
    return (Number.isNaN(difference) ? 0 : difference) || a.micros - b.micros;
  };
  for (const record of records) {
    const key = record.rootId || record.id,
      previous = latest.get(key);
    if (
      !previous ||
      record.version > previous.version ||
      (record.version === previous.version &&
        (createdOrder(record, previous) > 0 ||
          (createdOrder(record, previous) === 0 && record.id.localeCompare(previous.id) < 0)))
    )
      latest.set(key, record);
  }
  return [...latest.values()].sort((a, b) => a.id.localeCompare(b.id));
}
export function dailyTasks(records: OemRecord[], now: number) {
  if (!Number.isFinite(now) || !Number.isFinite(new Date(now).getTime()))
    throw Error('invalid_clock');
  return latestVisibleByRoot(records.filter((record) => record.kind === 'task'))
    .map((record) => {
      const deadline = parseDeadline(record.content.dueAt);
      return { record, deadline, category: deadlineCategory(deadline, now) };
    })
    .sort((a, b) => {
      const index = (key: TaskCategory) =>
        taskCategories.findIndex((category) => category.key === key);
      const at = (date: Deadline) =>
        date.kind === 'date' || date.kind === 'instant' ? date.at : Infinity;
      return (
        index(a.category) - index(b.category) ||
        at(a.deadline) - at(b.deadline) ||
        a.record.id.localeCompare(b.record.id)
      );
    });
}
